import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ShoppingBag, ArrowUpRight, Heart } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { API, useAuth } from "@/lib/auth";

export default function Shop() {
  const location = useLocation();
  const initialCat = new URLSearchParams(location.search).get("category") || "All";
  const [data, setData] = useState({ products: [], categories: [] });
  const [filter, setFilter] = useState(initialCat);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    axios.get(`${API}/shop/products`).then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(location.search).get("category");
    if (q) setFilter(q);
  }, [location.search]);

  useEffect(() => {
    if (user) axios.get(`${API}/me/wishlist`).then((r) => setWishlist(r.data)).catch(() => {});
    else setWishlist([]);
  }, [user]);

  const toggleWish = async (slug, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    const isIn = wishlist.includes(slug);
    if (isIn) {
      setWishlist((w) => w.filter((s) => s !== slug));
      await axios.delete(`${API}/me/wishlist/${slug}`);
    } else {
      setWishlist((w) => [...w, slug]);
      await axios.post(`${API}/me/wishlist`, { slug });
    }
  };

  const filtered =
    filter === "All"
      ? data.products
      : data.products.filter((p) => p.category === filter);

  return (
    <div data-testid="page-shop" className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid gap-8 lg:grid-cols-12 items-end mb-10">
          <div className="lg:col-span-8">
            <div className="overline">NøoksProRange</div>
            <h1 className="font-display text-5xl sm:text-7xl mt-4 tracking-tight text-balance">
              Salon-grade, <span className="italic font-light">home-ready.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[hsl(var(--onyx)/0.65)]">
              The NøoksProRange — the tools, hair and aftercare we use on our own
              clients — with free UK delivery over £50.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">
            {data.products.length} products · Free UK shipping over £50
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {["All", ...data.categories].map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                filter === c
                  ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                  : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.65)] hover:text-[hsl(var(--onyx))]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 text-center text-[hsl(var(--onyx)/0.5)]">Loading products…</div>
        ) : (
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p) => (
              <Item key={p.slug}>
                <Link
                  to={`/shop/${p.slug}`}
                  data-testid={`product-card-${p.slug}`}
                  className="group block rounded-[2rem] overflow-hidden bg-white border border-[hsl(var(--onyx)/0.06)] transition-shadow duration-500 hover:shadow-[0_20px_60px_-20px_rgba(20,10,15,0.15)]"
                >
                  <div className="relative aspect-square hover-zoom bg-blush">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    {user && (
                      <button
                        onClick={(e) => toggleWish(p.slug, e)}
                        data-testid={`wishlist-toggle-${p.slug}`}
                        className={`absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                          wishlist.includes(p.slug)
                            ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                            : "bg-white/90 backdrop-blur text-[hsl(var(--onyx))] hover:bg-white"
                        }`}
                        aria-label="Toggle wishlist"
                      >
                        <Heart size={15} fill={wishlist.includes(p.slug) ? "currentColor" : "none"} />
                      </button>
                    )}
                    {p.tag && !((typeof p.stock === "number") && p.stock <= 3) && (
                      <span className="absolute top-4 left-4 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-widest">
                        {p.tag}
                      </span>
                    )}
                    {typeof p.stock === "number" && p.stock === 0 && (
                      <span
                        data-testid={`product-soldout-${p.slug}`}
                        className="absolute top-4 left-4 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] px-3 py-1 text-[10px] uppercase tracking-widest"
                      >
                        Sold Out
                      </span>
                    )}
                    {typeof p.stock === "number" && p.stock > 0 && p.stock <= 3 && (
                      <span
                        data-testid={`product-lowstock-${p.slug}`}
                        className="absolute top-4 left-4 rounded-full bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))] px-3 py-1 text-[10px] uppercase tracking-widest"
                      >
                        Only {p.stock} left
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
                      {p.category}
                    </div>
                    <h3 className="font-display text-2xl mt-2 text-[hsl(var(--onyx))]">
                      {p.name}
                    </h3>
                    <p className="mt-2 text-sm text-[hsl(var(--onyx)/0.6)] line-clamp-2">
                      {p.shortDesc}
                    </p>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="font-display text-2xl">
                        {p.bundle ? "from " : ""}£{p.price.toFixed(2)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm text-[hsl(var(--onyx)/0.6)] group-hover:text-[hsl(var(--onyx))]">
                        View <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              </Item>
            ))}
          </Stagger>
        )}

        {!loading && filtered.length === 0 && (
          <div className="py-24 text-center text-[hsl(var(--onyx)/0.5)]">No products in this category.</div>
        )}

        <Reveal className="mt-24 rounded-[2rem] bg-blush-soft p-10 sm:p-14 flex flex-col sm:flex-row gap-6 items-center justify-between">
          <div>
            <ShoppingBag size={22} className="text-[hsl(var(--gold))]" />
            <h3 className="font-display text-3xl mt-3 tracking-tight">
              Free UK shipping on orders over £50.
            </h3>
            <p className="mt-2 text-sm text-[hsl(var(--onyx)/0.6)]">
              Otherwise a flat £4.95. Delivered within 3–5 working days.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
